/**
 * P3-2 — Inventaire des COMPOSANTS de vaisseau LIBRES du joueur (instances
 * lootées / récupérées au combat, non équipées). Aligné 1:1 sur
 * MeDtos.cs:MeShipComponentEntry / MeShipComponentsDto côté backend.
 *
 * `shipComponentToDisplayItem()` normalise une entrée en `DisplayItem` pour la
 * rendre via `<ItemDisplay>` (dossier d'image « components », contour de
 * rareté, tooltip riche) — exactement comme les objets.
 */
import type { MeItemAffix } from '@/types/inventory';
import type { DisplayItem, DisplayStat } from '@/types/itemDisplay';

export interface MeShipComponentEntry {
  /** Id de l'INSTANCE (table engines/weapons/.../hulls). */
  id: number;
  /** Catégorie de slot (Engine/Weapon/.../Hull). */
  category: string;
  /** Id du TYPE. */
  typeId: number;
  /** Code du type — sert d'image (dossier « components »). */
  code: string;
  label: string;
  /** Nom décoré de l'instance (libellé + affixes). */
  name: string;
  /** Image explicite (coques) ; null = retomber sur code. */
  imageCode?: string | null;
  /** Rareté 0..3. */
  rarity: number;
  quantity: number;
  /** Taille de slot (S/M/L/Capital). */
  size: string;
  /** Carburant / élément requis (libellé ressource) ; null si aucun. */
  fuelLabel?: string | null;
  /** Code ressource carburant/élément (image + couleur) ; null si aucun. */
  fuelCode?: string | null;
  /** Coques uniquement : type de vaisseau cible (filtre de compatibilité). */
  shipTypeId?: number | null;
  /** Optionnel : SET de composants (qualité orthogonale) ; non null = contour/
   *  fond VERT côté carte (prioritaire sur la rareté). null = hors set. */
  setId?: number | null;
  /** Niveau d'augmentation (atelier station) ; 0/absent = non augmenté. */
  augmentationLevel?: number;
  /** Cible ENGAGÉE par re-roll/enchant (verrou) ; null = pièce libre. */
  lockedTarget?: string | null;
  acquiredAt?: string | null;
  /** Stats figées de l'instance (clé lisible + valeur). */
  stats: MeItemAffix[];
  /** Affixes rollés figés (façon objet). */
  affixes: MeItemAffix[];
}

export interface MeShipComponentsDto {
  playerUniverseId: number;
  components: MeShipComponentEntry[];
}

/** Libellés FR des catégories de composant (ordre d'affichage = cet ordre). */
export const SHIP_COMPONENT_CATEGORY_LABELS: Record<string, string> = {
  Hull: 'Coque',
  Engine: 'Moteur',
  Weapon: 'Arme',
  Shield: 'Bouclier',
  Generator: 'Générateur',
  FuelTank: 'Réservoir',
  GemCell: 'Cellule de gemme',
  ElectricBattery: 'Pile électrique',
  Cockpit: 'Cockpit',
  CargoHold: 'Soute',
  Radar: 'Radar',
  Special: 'Spécial',
};

/** Ordre canonique des catégories pour l'affichage groupé. */
export const SHIP_COMPONENT_CATEGORY_ORDER = Object.keys(SHIP_COMPONENT_CATEGORY_LABELS);

export function shipComponentCategoryLabel(category: string): string {
  return SHIP_COMPONENT_CATEGORY_LABELS[category] ?? category;
}

/** Taille de slot (chaîne backend) -> nombre de symboles (1=petit … 4=capital). */
const SIZE_TO_NUM: Record<string, number> = { Small: 1, Medium: 2, Large: 3, Capital: 4 };

/** Catégories de composant ÉCHANGEABLES sur le marché (cf EMarketItemKind).
 *  Les autres (cellule, pile, module spécial, coque) ne sont pas échangeables. */
const TRADABLE_COMPONENT_CATEGORIES = new Set([
  'Engine', 'Weapon', 'Shield', 'Generator', 'FuelTank', 'Cockpit', 'CargoHold', 'Radar',
]);

/** Retire les suffixes de TAILLE/RESSOURCE entre parenthèses OU crochets du nom
 *  affiché (« Réservoir uranium (S) », « Laser [M] » → sans suffixe) : ces infos
 *  sont désormais portées par l'hexagone (taille + couleur) et la puce ressource.
 *  Couvre aussi les tailles longues (Small/Medium/Large/Capital). */
const SIZE_RES_SUFFIX = /\s*[([](?:s|m|l|cap|small|medium|large|capital|petit|moyen|grand|capital[e]?|p[ée]trole|uranium|deut[ée]rium|antimati[èe]re|saphir|[ée]meraude|diamant|rubis|topas|topaze|am[ée]thyste)[)\]]/gi;
/** Nettoie un nom de composant de son suffixe de taille/ressource. Exporté pour
 *  réutilisation par le studio (libellés de TYPES basiques). */
export function cleanComponentName(name: string): string {
  return name.replace(SIZE_RES_SUFFIX, '').trim();
}

/**
 * Un composant est-il BASIQUE (type IsFixed = équipement de départ, stats au
 * plancher de qualité, non looté) ? Détecté par le CODE du type, seul marqueur
 * porté jusqu'au front. Contrat garanti par `FixedBasicComponentSeeder` :
 *   - composants (moteur/arme/…) : le type dédié fixe porte « __fix_ » dans son
 *     code (les templates non dédiés sont forcés IsFixed=false, même si leur
 *     libellé dit « basique » — ex. `moteur_basique_m` est LOOTABLE) ;
 *   - coques : la coque basique d'un ShipType a le préfixe `hull_basic_`
 *     (IsFixed=true en seed ; les coques lootables ont un autre code).
 * Équivaut exactement au flag `IsFixed` côté base pour les types seedés.
 */
export function isBasicComponentCode(code: string | null | undefined): boolean {
  if (!code) return false;
  return code.includes('__fix_') || code.startsWith('hull_basic_');
}

/** Normalise une instance de composant en DisplayItem (rendu façon objet). */
export function shipComponentToDisplayItem(entry: MeShipComponentEntry): DisplayItem {
  const stats: DisplayStat[] = [];
  for (const s of entry.stats)
    stats.push(makeComponentStat(s, true));
  for (const a of entry.affixes)
    stats.push(makeComponentStat(a, false));
  return {
    id: `comp:${entry.category}:${entry.id}`,
    code: entry.code,
    imageCode: entry.imageCode,
    imageFolder: 'components',
    name: cleanComponentName(entry.name || entry.label),
    description: shipComponentCategoryLabel(entry.category),
    kind: null,
    typeLabel: shipComponentCategoryLabel(entry.category),
    rarity: entry.rarity,
    isBasic: isBasicComponentCode(entry.code),   // badge « Basique » (type IsFixed)
    setId: entry.setId ?? null,   // contour/fond VERT du set, prioritaire sur la rareté
    augmentationLevel: entry.augmentationLevel ?? 0,   // étoiles de forge ★
    quantity: entry.quantity,
    acquiredAt: entry.acquiredAt,
    slotSize: SIZE_TO_NUM[entry.size] ?? null,
    fuelLabel: entry.fuelLabel ?? null,
    fuelCode: entry.fuelCode ?? null,
    isTradable: TRADABLE_COMPONENT_CATEGORIES.has(entry.category),
    stats,
  };
}

/** Le backend fournit l'effet CLAIR (label d'attribut + isPercent + sens) ;
 *  on retombe sur la clé si un ancien payload n'a pas ces champs. Exporté pour
 *  que la fiche de vaisseau équipé (ShipDetailModal, stats du ShipModuleView)
 *  mappe ses stats IDENTIQUEMENT à l'inventaire. */
export function makeComponentStat(a: MeItemAffix, isPrimary: boolean): DisplayStat {
  return {
    key: a.key,
    label: a.label || a.key,
    value: a.value,
    isPercent: a.isPercent ?? false,
    higherIsBetter: a.higherIsBetter ?? true,
    isPrimary,
    min: a.min ?? null,
    max: a.max ?? null,
  };
}
